import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Search, 
  Calendar, 
  Clock, 
  MapPin, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  Loader2,
  Home,
  ArrowLeft,
  FileText,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TrackBookingPage = () => {
  const [searchParams] = useSearchParams();
  const initialRef = searchParams.get('ref') || '';
  
  const [confirmationNumber, setConfirmationNumber] = useState(initialRef);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate) return '';
    const start = formatDate(startDate);
    if (!endDate || endDate === startDate) return start;
    const end = formatDate(endDate);
    return `${start} to ${end}`;
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'approved':
        return {
          icon: CheckCircle2,
          label: 'Approved',
          color: 'bg-cameroon-green text-white',
          description: 'Your event has been approved and is now on the public calendar!'
        };
      case 'rejected':
        return {
          icon: XCircle,
          label: 'Rejected',
          color: 'bg-red-600 text-white',
          description: 'Unfortunately, your event request was not approved. Please contact admin for more details.'
        };
      case 'pending':
      default:
        return {
          icon: Clock,
          label: 'Pending Review',
          color: 'bg-cameroon-yellow text-black',
          description: 'Your event is being reviewed by our admin team. You will be notified once a decision is made.'
        };
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    
    if (!confirmationNumber.trim()) {
      toast.error('Please enter a confirmation number');
      return;
    }

    setLoading(true);
    setError('');
    setBooking(null);
    setSearched(true);

    try {
      const response = await axios.get(`${API_URL}/activities/track/${confirmationNumber.trim().toUpperCase()}`);
      setBooking(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Booking not found. Please check your confirmation number and try again.');
      } else {
        setError('An error occurred while searching. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const statusConfig = booking ? getStatusConfig(booking.status) : null;
  const StatusIcon = statusConfig?.icon;

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <Navbar />
      
      <main className="flex-1 px-4 py-8 sm:py-12">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cameroon-green/20 mb-4">
              <Search className="w-8 h-8 text-cameroon-green" />
            </div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold text-white mb-2">
              Track Your Booking
            </h1>
            <p className="text-white/70 text-sm sm:text-base">
              Enter your confirmation number to check the status of your event request
            </p>
          </div>

          {/* Search Form */}
          <Card className="glass border-white/10 mb-6">
            <CardContent className="p-6">
              <form onSubmit={handleSearch} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm text-white/70">Confirmation Number</label>
                  <div className="flex gap-2">
                    <Input
                      value={confirmationNumber}
                      onChange={(e) => setConfirmationNumber(e.target.value.toUpperCase())}
                      placeholder="e.g., CCA-2026-ABC123"
                      className="font-mono text-lg bg-white/5 border-white/20 text-white placeholder:text-white/40"
                      data-testid="confirmation-input"
                    />
                    <Button 
                      type="submit"
                      disabled={loading}
                      className="bg-cameroon-green hover:bg-cameroon-green/90 px-6"
                      data-testid="search-btn"
                    >
                      {loading ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                      ) : (
                        <Search className="w-5 h-5" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-white/50">
                    Your confirmation number was provided when you submitted your booking
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Error State */}
          {searched && error && (
            <Card className="bg-red-900/20 border-red-500/30 mb-6">
              <CardContent className="p-6 text-center">
                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-white mb-2">Booking Not Found</h3>
                <p className="text-white/70 text-sm mb-4">{error}</p>
                <Button 
                  variant="outline" 
                  className="border-white/20 text-white hover:bg-white/10"
                  onClick={() => {
                    setConfirmationNumber('');
                    setSearched(false);
                    setError('');
                  }}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try Again
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Booking Result */}
          {booking && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
              {/* Status Card */}
              <Card className={`${statusConfig.color} border-0`}>
                <CardContent className="p-6 text-center">
                  <StatusIcon className="w-12 h-12 mx-auto mb-3" />
                  <h2 className="text-2xl font-bold mb-2">{statusConfig.label}</h2>
                  <p className="text-sm opacity-90">{statusConfig.description}</p>
                </CardContent>
              </Card>

              {/* Confirmation Number */}
              <Card className="glass border-white/10">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="text-xs text-white/50 uppercase tracking-wide">Confirmation Number</p>
                    <p className="text-xl font-mono font-bold text-cameroon-green">{booking.confirmation_number}</p>
                  </div>
                  <Badge 
                    className={`${
                      booking.status === 'approved' ? 'bg-cameroon-green' : 
                      booking.status === 'rejected' ? 'bg-red-600' : 
                      'bg-cameroon-yellow text-black'
                    }`}
                  >
                    {booking.status}
                  </Badge>
                </CardContent>
              </Card>

              {/* Event Details */}
              <Card className="glass border-white/10">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-white text-lg mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-cameroon-green" />
                    Event Details
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="bg-white/5 rounded-lg p-4">
                      <h4 className="text-xl font-semibold text-white mb-1">{booking.title}</h4>
                      <Badge variant="outline" className="text-xs text-cameroon-yellow border-cameroon-yellow/30">
                        {booking.category}
                      </Badge>
                      {booking.description && (
                        <p className="text-white/70 text-sm mt-3">{booking.description}</p>
                      )}
                    </div>

                    <div className="grid gap-3">
                      <div className="flex items-start gap-3 text-white/80">
                        <Calendar className="w-5 h-5 text-cameroon-green flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-white/50 uppercase tracking-wide">Date</p>
                          <p className="font-medium">{formatDateRange(booking.date, booking.end_date)}</p>
                          {booking.end_date && booking.end_date !== booking.date && (
                            <Badge variant="secondary" className="mt-1 text-xs bg-white/10">
                              Multi-day Event
                            </Badge>
                          )}
                        </div>
                      </div>

                      {booking.time && (
                        <div className="flex items-start gap-3 text-white/80">
                          <Clock className="w-5 h-5 text-cameroon-yellow flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-white/50 uppercase tracking-wide">Time</p>
                            <p className="font-medium">{booking.time}{booking.end_time && ` - ${booking.end_time}`}</p>
                          </div>
                        </div>
                      )}

                      {booking.location && (
                        <div className="flex items-start gap-3 text-white/80">
                          <MapPin className="w-5 h-5 text-cameroon-red flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-white/50 uppercase tracking-wide">Location</p>
                            <p className="font-medium">{booking.location}</p>
                          </div>
                        </div>
                      )}

                      <div className="flex items-start gap-3 text-white/80">
                        <FileText className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs text-white/50 uppercase tracking-wide">Organization</p>
                          <p className="font-medium">{booking.organization_name}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Timestamps */}
              <Card className="glass border-white/10">
                <CardContent className="p-4">
                  <div className="flex flex-wrap gap-4 text-sm text-white/60">
                    <div>
                      <span className="text-white/40">Submitted:</span>{' '}
                      {new Date(booking.created_at).toLocaleDateString('en-US', {
                        month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                      })}
                    </div>
                    {booking.approved_at && (
                      <div>
                        <span className="text-white/40">Approved:</span>{' '}
                        {new Date(booking.approved_at).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
                        })}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Navigation */}
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/calendar" className="flex-1">
              <Button 
                variant="outline" 
                className="w-full border-white/20 text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Calendar
              </Button>
            </Link>
            <Link to="/" className="flex-1">
              <Button 
                className="w-full bg-cameroon-green hover:bg-cameroon-green/90"
              >
                <Home className="w-4 h-4 mr-2" />
                Go to Home
              </Button>
            </Link>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-4">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Council of Cultural Associations Montreal
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TrackBookingPage;
